import React, {useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {getValidation} from '../CreateRecommend/RecommendSourceDestination/validators';
import {NewPlanForm} from './NewPlanForm';
import OWNERSHIP from '../../../constants/Ownership';
import {WrappedNewSubPlans} from './ListNewSubPlans';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {newPlanFields, setUpNewFields} from './setUpNewFields';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {
  getIsTransactBond,
  useDeriveIsLowesPension,
  useDeriveIsTransactBond,
  type NewPlanFormikPrefix,
} from '../hooks/setUpNewHooks';

export {getIsTransactBond, useDeriveIsLowesPension, useDeriveIsTransactBond};

const getNewPlanTitle = (mainPlanType: string) => {
  let article = 'A';
  let planTitle = '';
  switch (mainPlanType) {
    case 'isa':
      article = 'An';
      planTitle = mainPlanType.toUpperCase();
      break;
    case 'jisa':
      planTitle = mainPlanType.toUpperCase();
      break;
    case 'jsipp':
      planTitle = 'Junior SIPP';
      break;
    case 'gia':
      planTitle = mainPlanType.toUpperCase();
      break;
    default:
      planTitle = mainPlanType;
      break;
  }

  return `${article} ${planTitle}`;
};

export const NewPlan: React.FC<{
  contactName: string;
  formikKeyPrefix: NewPlanFormikPrefix;
  context: 'setUpNew' | 'transfer' | 'contribute';
}> = ({contactName, formikKeyPrefix, context}) => {
  const validationContext = useMemo(() => {
    if (context === 'contribute') {
      return 'contribute.newPlan';
    }
    if (context === 'transfer') {
      return 'transfer.newPlan';
    }
    return 'setUpNew';
  }, [context]);
  const [__, setUpNewFieldMeta, ___] = useAutoField<setUpNewFields | undefined>(
    validationContext
  );
  const {values} = useAutoForm<recommendFieldsType>();
  const {mainPlanType, goal} = values;
  const {isSPPDA} = useRecommendContext();
  const [selected, setSelected] = useState(true);
  const goalText = isSPPDA ? '' : `- Associated goal: ${goal?.name || 'N/A'}`;

  const isLowesPension = useDeriveIsLowesPension(formikKeyPrefix);
  const isTransactBond = useDeriveIsTransactBond(formikKeyPrefix);

  return (
    <Box data-test="choice-option" width="100%">
      <RecommendPlanForm
        reccId=""
        id="notused"
        summary={
          <Typography>
            {`${getNewPlanTitle(mainPlanType as string) || ''} for ${
              values?.contact === OWNERSHIP.JOINT_CODE
                ? OWNERSHIP.JOINT_LIVES
                : contactName
            } ${goalText}`}
          </Typography>
        }
        details={
          <Box flex={1}>
            <NewPlanForm
              formikKeyPrefix={formikKeyPrefix}
              isLowesPension={isLowesPension}
              isTransactBond={isTransactBond}
            />
          </Box>
        }
        sameReview={false}
        validation={getValidation(setUpNewFieldMeta)}
        subItems={
          hasWrapper(mainPlanType) && !isLowesPension && !isTransactBond ? (
            <WrappedNewSubPlans context={context} formikKeyPrefix={formikKeyPrefix} />
          ) : undefined
        }
        isSelected={selected}
        isSubplan={false}
        handleChange={() => setSelected((prev) => !prev)}
      />
    </Box>
  );
};
